import { create } from 'zustand';
import type { PathChange, RecalibrationChange } from '../types/adaptive';
import {
  getAdjustmentMessage,
  dismissAdjustmentMessage,
  onProfileRecalibrated,
} from '../services/adaptive';

interface AdaptiveState {
  adjustmentMessage: string | null;
  adjustmentChanges: PathChange[] | null;
  showAdjustmentBanner: boolean;

  recentRecalibration: RecalibrationChange[] | null;
  diagnosticRetakeSuggested: boolean;
  profileVersion: number | null;

  setAdjustmentMessage: (msg: string | null) => void;
  loadAdjustmentState: (pathId: string) => Promise<void>;
  dismissAdjustment: (pathId: string) => Promise<void>;
  listenToRecalibrations: (userId: string) => () => void;
  setDiagnosticRetakeSuggested: (suggested: boolean) => void;
  reset: () => void;
}

export const useAdaptiveStore = create<AdaptiveState>((set) => ({
  adjustmentMessage: null,
  adjustmentChanges: null,
  showAdjustmentBanner: false,
  recentRecalibration: null,
  diagnosticRetakeSuggested: false,
  profileVersion: null,

  setAdjustmentMessage: (msg) => set({
    adjustmentMessage: msg,
    showAdjustmentBanner: !!msg,
  }),

  loadAdjustmentState: async (pathId) => {
    const message = await getAdjustmentMessage(pathId);
    set({
      adjustmentMessage: message,
      showAdjustmentBanner: !!message,
    });
  },

  dismissAdjustment: async (pathId) => {
    await dismissAdjustmentMessage(pathId);
    set({
      adjustmentMessage: null,
      showAdjustmentBanner: false,
    });
  },

  listenToRecalibrations: (userId) => {
    return onProfileRecalibrated(userId, (changes) => {
      if (changes.length > 0) {
        set({ recentRecalibration: changes });
      }
    });
  },

  setDiagnosticRetakeSuggested: (suggested) => set({ diagnosticRetakeSuggested: suggested }),

  reset: () => set({
    adjustmentMessage: null,
    adjustmentChanges: null,
    showAdjustmentBanner: false,
    recentRecalibration: null,
    diagnosticRetakeSuggested: false,
    profileVersion: null,
  }),
}));
